import { Injectable, signal, computed } from '@angular/core';
import {
  CicloType, ConsultorRef, RoteiroTemplate, CICLO_STEPS, CicloStepInfo,
  VisitCycleDetailsDto, VisitCycleDayDto, VisitCycleType,
  VisitCycleStatus, VisitCycleExecutionStatus,
  EditingScope, ScopeDropdownItem
} from '../models/ciclo';
import { SummaryItem } from '../../../roteirizacao/wizard/components/roteiro-summary/roteiro-summary.component';

export interface Step1Data {
  roteiroIds: number[];
  roteiroNames: string[];
  consultorId: number | null;
  consultorName: string;
  tipoCiclo: CicloType;
  dataInicio: string;
  dataFim: string | null;
  evitarFinalSemana: boolean;
  considerarFeriados: boolean;
}

export interface Step1FormState {
  roteiroInput: string;
  consultorInput: string;
  selectedRoteiros: RoteiroTemplate[];
  selectedConsultor: ConsultorRef | null;
}

const DEFAULT_STEP1_DATA: Step1Data = {
  roteiroIds: [],
  roteiroNames: [],
  consultorId: null,
  consultorName: '',
  tipoCiclo: 'Continuo',
  dataInicio: '',
  dataFim: null,
  evitarFinalSemana: true,
  considerarFeriados: true
};

const DEFAULT_STEP1_FORM_STATE: Step1FormState = {
  roteiroInput: '',
  consultorInput: '',
  selectedRoteiros: [],
  selectedConsultor: null
};

@Injectable()
export class CicloWizardStore {
  private readonly _currentStep = signal(1);
  private readonly _maxStepReached = signal(1);
  private readonly _step1Data = signal<Step1Data>(DEFAULT_STEP1_DATA);
  private readonly _step1FormState = signal<Step1FormState>(DEFAULT_STEP1_FORM_STATE);
  private readonly _cicloId = signal<number | null>(null);
  private readonly _steps = signal<CicloStepInfo[]>(CICLO_STEPS);
  private readonly _cycleDetails = signal<VisitCycleDetailsDto | null>(null);
  private readonly _cycleDays = signal<VisitCycleDayDto[]>([]);
  private readonly _editingScope = signal<EditingScope>('template');

  readonly currentStep = this._currentStep.asReadonly();
  readonly maxStepReached = this._maxStepReached.asReadonly();
  readonly step1Data = this._step1Data.asReadonly();
  readonly step1FormState = this._step1FormState.asReadonly();
  readonly cicloId = this._cicloId.asReadonly();
  readonly isEditMode = computed(() => this._cicloId() !== null);
  readonly steps = this._steps.asReadonly();
  readonly cycleDetails = this._cycleDetails.asReadonly();
  readonly cycleDays = this._cycleDays.asReadonly();

  // editingScope resolvido. Se _editingScope for 'template' mas o template
  // está frozen (sem próxima projeção), resolve pra primeira exec editável
  // (InProgress > primeira Planned). Derivação (não signal mutável) evita state
  // stale — qualquer consumer que lê editingScope() sempre vê o scope efetivo,
  // independente da ordem de setCycleDetails/refreshCycleMetadata/setEditingScope.
  readonly editingScope = computed<EditingScope>(() => {
    const raw = this._editingScope();
    if (raw === 'template' && this.isTemplateFrozen()) {
      const execs = this.editableExecutions();
      if (execs.length > 0) {
        const preferred = execs.find(e => e.status === VisitCycleExecutionStatus.InProgress)
          ?? execs[0];
        return { executionId: preferred.id };
      }
    }
    return raw;
  });

  readonly isActiveCycle = computed(() => {
    const status = this._cycleDetails()?.status;
    return status === VisitCycleStatus.Active || status === VisitCycleStatus.Paused;
  });

  readonly isPausedCycle = computed(() => this._cycleDetails()?.status === VisitCycleStatus.Paused);

  // Usa editingScope resolvido (não _editingScope raw) pra respeitar
  // o auto-resolve de 'template' → exec em ciclos frozen.
  readonly isEditingExecution = computed(() => this.editingScope() !== 'template');

  readonly selectedExecutionId = computed(() => {
    const scope = this.editingScope();
    return scope !== 'template' ? scope.executionId : null;
  });

  readonly editableExecutions = computed(() => this._cycleDetails()?.editableExecutions ?? []);

  // Template scope perde sentido quando o ciclo não tem próxima
  // execução projetada (pontual materializado, ou recorrente cuja próxima
  // projeção passa do endDate). As datas do template são "fósseis"
  // da última projeção e confundem o admin. Hide > read-only porque o scope
  // Execução cobre tudo que o admin pode editar nesse estado.
  // Defense-in-depth: backend guard (rotate 400), drag-lock continuam como guards.
  readonly isTemplateFrozen = computed(() => {
    const cycle = this._cycleDetails();
    if (!cycle) return false;
    if (!this.isActiveCycle()) return false;
    // Pontual materializado nunca tem projeção futura — congela direto por tipo.
    // A heurística de datas abaixo inferia isso indiretamente e vazava no boundary
    // floor == endDate (folga de 1 dia entre a execução e o endDate), deixando o
    // template editável num pontual de 1 dia já em execução (ciclo 65, staging).
    if (cycle.type === VisitCycleType.OneTime && this.editableExecutions().length > 0) return true;
    if (cycle.repeatIntervalValue === null || cycle.repeatIntervalValue === undefined) return true;
    if (cycle.endDate && cycle.templateMinAssignableDate
        && cycle.templateMinAssignableDate > cycle.endDate) return true;
    return false;
  });

  readonly dropdownItems = computed<ScopeDropdownItem[]>(() => {
    if (!this.isActiveCycle()) return [];

    const execs = this.editableExecutions();
    const items: ScopeDropdownItem[] = [];

    // Esconde template quando frozen — admin vê só execuções.
    if (!this.isTemplateFrozen()) {
      items.push({ label: 'Template: Todos os Ciclos', value: 'template' });
    }

    for (const exec of execs) {
      const start = this.formatDate(exec.plannedStartDate);
      const end = this.formatDate(exec.plannedEndDate);
      const dateRange = `${start} a ${end}`;
      const isInProgress = exec.status === VisitCycleExecutionStatus.InProgress;
      const label = isInProgress
        ? `Ciclo Atual: ${dateRange}`
        : `Próximo Ciclo: ${dateRange}`;
      items.push({ label, value: { executionId: exec.id }, dateRange });
    }

    return items;
  });

  readonly summaryItems = computed<SummaryItem[]>(() => {
    const data = this._step1Data();
    const consultantName = this.getScopedConsultantName();
    const roteirosLabel = data.roteiroNames.length > 0 
      ? data.roteiroNames.join(' → ') 
      : '-';
    return [
      { label: 'Roteiros', value: roteirosLabel },
      { label: 'Consultor', value: consultantName },
      { label: 'Tipo de Ciclo', value: data.tipoCiclo === 'Continuo' ? 'Contínuo' : 'Pontual' },
      { label: 'Período', value: this.formatPeriodo(data) }
    ];
  });

  readonly calendarSummaryItems = computed<SummaryItem[]>(() => {
    const data = this._step1Data();
    const days = this._cycleDays();
    const assignedDays = days.filter(d => d.isAssigned).length;
    // Resolvido (editingScope, não _editingScope) pra mostrar período
    // da exec quando template está frozen.
    const scope = this.editingScope();
    const execs = this.editableExecutions();
    const consultantName = this.getScopedConsultantName();
    const roteirosLabel = data.roteiroNames.length > 0
      ? data.roteiroNames.join(' → ')
      : '-';

    // When editing an execution, show execution period and hide cycle type
    if (scope !== 'template' && execs.length > 0) {
      const exec = execs.find(e => e.id === scope.executionId);
      const periodo = exec
        ? `${this.formatDate(exec.plannedStartDate)} a ${this.formatDate(exec.plannedEndDate)}`
        : '-';
      return [
        { label: 'Roteiros', value: roteirosLabel },
        { label: 'Consultor', value: consultantName },
        { label: 'Período', value: periodo }
      ];
    }

    return [
      { label: 'Roteiros', value: roteirosLabel },
      { label: 'Consultor', value: consultantName },
      { label: 'Tipo de Ciclo', value: data.tipoCiclo === 'Continuo' ? 'Contínuo' : 'Pontual' },
      { label: 'Período', value: this.formatPeriodoFromStartDate(data) },
      { label: 'Dias Atribuídos', value: `${assignedDays}/${days.length}` }
    ];
  });

  private getScopedConsultantName(): string {
    // Resolvido (editingScope, não _editingScope) pra respeitar frozen.
    const scope = this.editingScope();
    if (scope !== 'template') {
      const execution = this.editableExecutions().find(exec => exec.id === scope.executionId);
      if (execution?.consultantName) {
        return execution.consultantName;
      }
    }

    return this._step1Data().consultorName || '-';
  }

  readonly allDaysAssigned = computed(() => {
    const days = this._cycleDays();
    return days.length > 0 && days.every(d => d.isAssigned);
  });

  private formatPeriodo(data: Step1Data): string {
    if (!data.dataInicio) return 'Não Definido';
    
    const inicio = this.formatDate(data.dataInicio);
    if (data.tipoCiclo === 'Continuo') {
      return `Inicia-se em ${inicio}`;
    }
    
    if (!data.dataFim) return `A partir de ${inicio}`;
    const fim = this.formatDate(data.dataFim);
    return `${inicio} - ${fim}`;
  }

  private formatPeriodoFromStartDate(data: Step1Data): string {
    if (!data.dataInicio) return 'Não Definido';
    
    const inicio = this.formatDate(data.dataInicio);
    if (data.tipoCiclo === 'Continuo') {
      return `Inicia-se em ${inicio}`;
    }
    
    if (!data.dataFim) return `Inicia-se em ${inicio}`;
    const fim = this.formatDate(data.dataFim);
    return `${inicio} a ${fim}`;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  }

  setCurrentStep(step: number): void {
    this._currentStep.set(step);
    if (step > this._maxStepReached()) {
      this._maxStepReached.set(step);
    }
  }

  updateStep1Data(data: Partial<Step1Data>): void {
    this._step1Data.update(current => ({ ...current, ...data }));
  }

  updateStep1FormState(state: Partial<Step1FormState>): void {
    this._step1FormState.update(current => ({ ...current, ...state }));
  }

  setCicloId(id: number | null): void {
    // Quando o id muda (admin navega entre ciclos sem sair do
    // wizard), reseta state dependente do ciclo antigo antes de sinalizar o novo.
    // Evita que o effect do step-ciclo-calendario dispare `getExecutionDays` com
    // um `executionId` do ciclo anterior e receba 404.
    const previous = this._cicloId();
    if (previous !== null && previous !== id) {
      this._editingScope.set('template');
      this._cycleDetails.set(null);
      this._cycleDays.set([]);
    }
    this._cicloId.set(id);
  }

  setEditingScope(scope: EditingScope): void {
    this._editingScope.set(scope);
  }

  // Reset baseado no `details` passado direto, sem depender
  // de `_cycleDetails()` atualizado. Necessário pra quebrar a race que expõe
  // scope stale + details novo ao effect do step-ciclo-calendario — cenário:
  // ciclo A tem scope={executionId:N}; navegar pra ciclo B (sem exec N)
  // dispararia `_cycleDetails.set(cycleB)` ANTES do reset, e o effect chamaria
  // `getExecutionDays(cycleId=B, executionId=N)` → 404. Ao resetar com o
  // `details` novo ANTES de sinalizar `_cycleDetails`, o effect nunca vê o
  // par inconsistente.
  private resetScopeIfNotEditableForDetails(details: VisitCycleDetailsDto | null): void {
    const scope = this._editingScope();
    if (scope === 'template') return;

    const editableExecs = details?.editableExecutions ?? [];
    const stillEditable = editableExecs.some(exec => exec.id === scope.executionId);
    if (!stillEditable) {
      // Volta pra 'template' (backing signal). O `editingScope` computed resolve
      // pra primeira exec quando template está frozen.
      this._editingScope.set('template');
    }
  }

  setCycleDetails(details: VisitCycleDetailsDto): void {
    // Reset ANTES de sinalizar details novo (quebra race do effect).
    this.resetScopeIfNotEditableForDetails(details);

    this._cycleDetails.set(details);
    this._cycleDays.set(details.days || []);

    if (this._maxStepReached() < 2) {
      this._maxStepReached.set(2);
    }

    // Update step1Data from cycle details
    const roteiroNames = details.visitPlans?.map(vp => vp.visitPlanName) || details.visitPlanNames || [];
    const roteiroIds = details.visitPlans?.map(vp => vp.visitPlanId) || [];

    this._step1Data.update(current => ({
      ...current,
      roteiroIds,
      roteiroNames,
      consultorId: details.consultantId,
      consultorName: details.consultantName,
      tipoCiclo: details.type === VisitCycleType.Continuous ? 'Continuo' : 'Pontual',
      dataInicio: details.startDate,
      dataFim: details.endDate || null,
      evitarFinalSemana: details.avoidWeekends,
      considerarFeriados: details.considerHolidays
    }));
  }

  /**
   * Refreshes cycle metadata (executions, dropdown labels, summary) without
   * overwriting _cycleDays. Used after execution save to update dropdown
   * labels and execution status without resetting the current scope's days.
   */
  refreshCycleMetadata(details: VisitCycleDetailsDto): void {
    // Reset ANTES de sinalizar details novo (quebra race do effect).
    this.resetScopeIfNotEditableForDetails(details);
    this._cycleDetails.set(details);

    const roteiroNames = details.visitPlans?.map(vp => vp.visitPlanName) || details.visitPlanNames || [];
    const roteiroIds = details.visitPlans?.map(vp => vp.visitPlanId) || [];

    this._step1Data.update(current => ({
      ...current,
      roteiroIds,
      roteiroNames,
      consultorId: details.consultantId,
      consultorName: details.consultantName,
      tipoCiclo: details.type === VisitCycleType.Continuous ? 'Continuo' : 'Pontual',
      dataInicio: details.startDate,
      dataFim: details.endDate || null,
      evitarFinalSemana: details.avoidWeekends,
      considerarFeriados: details.considerHolidays
    }));
  }

  updateCycleDays(days: VisitCycleDayDto[]): void {
    this._cycleDays.set(days);
  }

  reset(): void {
    this._currentStep.set(1);
    this._maxStepReached.set(1);
    this._step1Data.set({ ...DEFAULT_STEP1_DATA });
    this._step1FormState.set({ ...DEFAULT_STEP1_FORM_STATE });
    this._cicloId.set(null);
    this._cycleDetails.set(null);
    this._cycleDays.set([]);
    this._editingScope.set('template');
  }

  resetStep1Data(): void {
    this._step1Data.set({ ...DEFAULT_STEP1_DATA });
    this._step1FormState.set({ ...DEFAULT_STEP1_FORM_STATE });
  }

  isStepClickable(stepNumber: number): boolean {
    return stepNumber <= this._maxStepReached() && stepNumber !== this._currentStep();
  }

  isStepCompleted(stepNumber: number): boolean {
    return stepNumber < this._currentStep() || (stepNumber <= this._maxStepReached() && stepNumber !== this._currentStep());
  }
}
